import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

const TOKEN_KEY = '1os_access_token';
const USER_KEY = '1os_user_info';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessContext, setAccessContext] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      const stored = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
      setUser({
        ...data,
        user_name: stored.user_name || '',
        user_email: stored.user_email || data.user_email || '',
        org_name: stored.org_name || '',
      });
      setAccessContext(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
      setAccessContext(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify({
      user_name: data.user_name,
      user_email: data.user_email,
      org_name: data.org_name,
      org_id: data.org_id,
      current_outlet_id: data.current_outlet_id,
      is_org_admin: data.is_org_admin,
    }));
    // Fetch access context (capabilities, outlet_ids, etc.)
    const { data: me } = await api.get('/auth/me');
    const userObj = {
      ...me,
      user_name: data.user_name,
      user_email: data.user_email,
      org_name: data.org_name,
    };
    setUser(userObj);
    setAccessContext(me);
    return userObj;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* Non-critical */
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setAccessContext(null);
  };

  const hasCapability = useCallback((cap) => {
    if (!accessContext) return false;
    return (accessContext.capabilities || []).includes(cap);
  }, [accessContext]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      checkAuth,
      accessContext,
      hasCapability,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
